import React, { useCallback, useEffect, useState } from 'react';
import {
  Building2, LogIn, RefreshCw, MoreHorizontal, PauseCircle, PlayCircle,
  Trash2, CreditCard, X, AlertTriangle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import { useAuth } from '../../context/AuthContext';

const PLAN_OPTIONS = ['free', 'starter', 'pro', 'enterprise'];

const STATUS_STYLES = {
  trial: 'bg-purple-50 text-purple-700',
  active: 'bg-emerald-50 text-emerald-700',
  past_due: 'bg-amber-50 text-amber-700',
  suspended: 'bg-rose-50 text-rose-700',
  canceled: 'bg-slate-100 text-slate-500',
};

const fmtUSD = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const OrganizationsPanel = () => {
  const navigate = useNavigate();
  const { isPlatformAdmin, startImpersonation } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [planModal, setPlanModal] = useState(null);
  const [suspendModal, setSuspendModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [working, setWorking] = useState(false);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/platform/organizations');
      setOrgs(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const handleImpersonate = async (org) => {
    const reason = window.prompt(
      `Open a support session for "${org.name}"?\n\nEnter a reason (visible in audit log):`,
      '',
    );
    if (reason === null) return;
    setImpersonatingId(org.id);
    try {
      await startImpersonation(org.id, reason || null);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start support session');
    } finally {
      setImpersonatingId(null);
    }
  };

  const handleReactivate = async (org) => {
    if (!window.confirm(`Reactivate "${org.name}"?`)) return;
    setWorking(true);
    try {
      await apiClient.post(`/api/platform/organizations/${org.id}/reactivate`);
      await fetchOrgs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Reactivate failed');
    } finally {
      setWorking(false);
      setOpenMenuId(null);
    }
  };

  const submitPlanChange = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      await apiClient.put(`/api/platform/organizations/${planModal.id}/plan`, {
        plan: planModal.plan,
        monthly_amount: parseFloat(planModal.monthly_amount) || 0,
        reason: planModal.reason || null,
      });
      setPlanModal(null);
      await fetchOrgs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Plan change failed');
    } finally {
      setWorking(false);
    }
  };

  const submitSuspend = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      await apiClient.post(`/api/platform/organizations/${suspendModal.id}/suspend`, {
        reason: suspendModal.reason,
      });
      setSuspendModal(null);
      await fetchOrgs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Suspend failed');
    } finally {
      setWorking(false);
    }
  };

  const submitDelete = async () => {
    if (deleteModal.confirmText !== deleteModal.name) {
      setError('Type the organization name exactly to confirm.');
      return;
    }
    setWorking(true);
    try {
      await apiClient.delete(`/api/platform/organizations/${deleteModal.id}?cascade=true`);
      setDeleteModal(null);
      await fetchOrgs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">Organizations</h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400">Every tenant on mySMS360. Lifecycle controls are admin-only.</p>
        </div>
        <button
          onClick={fetchOrgs}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-[13px] rounded-lg px-4 py-3 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[13px] text-slate-500">Loading…</div>
        ) : orgs.length === 0 ? (
          <div className="p-8 text-center text-[13px] text-slate-500">
            <Building2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            No organizations yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 dark:bg-slate-800/50 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Organization</th>
                  <th className="px-4 py-2 text-left font-semibold">Plan</th>
                  <th className="px-4 py-2 text-left font-semibold">Status</th>
                  <th className="px-4 py-2 text-right font-semibold">MRR</th>
                  <th className="px-4 py-2 text-left font-semibold">Owner</th>
                  <th className="px-4 py-2 text-right font-semibold">Facilities</th>
                  <th className="px-4 py-2 text-right font-semibold">Customers</th>
                  <th className="px-4 py-2 text-left font-semibold">Started</th>
                  <th className="px-4 py-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {orgs.map((org) => {
                  const statusStyle = STATUS_STYLES[org.status] || 'bg-slate-100 text-slate-500';
                  return (
                    <tr key={org.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 relative">
                      <td className="px-4 py-2.5 font-medium text-slate-900 dark:text-slate-100">
                        {org.name}
                        {org.suspended_reason && (
                          <p className="text-[11px] text-rose-500 mt-0.5" title={org.suspended_reason}>
                            Suspended: {org.suspended_reason}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400 uppercase text-[11px] tracking-wider">{org.plan}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded font-medium uppercase tracking-wider ${statusStyle}`}>
                          {org.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-700 dark:text-slate-300">{fmtUSD(org.monthly_amount)}</td>
                      <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{org.owner_email || '—'}</td>
                      <td className="px-4 py-2.5 text-right text-slate-700 dark:text-slate-300">{org.facility_count}</td>
                      <td className="px-4 py-2.5 text-right text-slate-700 dark:text-slate-300">{org.customer_count}</td>
                      <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400 text-[11px]">{fmtDate(org.plan_started_at || org.created_at)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleImpersonate(org)}
                            disabled={impersonatingId === org.id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[12px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-50"
                            title="Start a support session as this organization"
                          >
                            <LogIn className="h-3 w-3" />
                            {impersonatingId === org.id ? '…' : 'Impersonate'}
                          </button>
                          {isPlatformAdmin() && (
                            <div className="relative">
                              <button
                                onClick={() => setOpenMenuId(openMenuId === org.id ? null : org.id)}
                                className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </button>
                              {openMenuId === org.id && (
                                <div className="absolute right-0 top-7 z-10 w-44 bg-white border border-slate-200 rounded-lg shadow-lg py-1">
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setPlanModal({
                                        id: org.id,
                                        name: org.name,
                                        plan: org.plan,
                                        monthly_amount: org.monthly_amount || 0,
                                        reason: '',
                                      });
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                  >
                                    <CreditCard className="h-3.5 w-3.5" />
                                    Change plan
                                  </button>
                                  {org.status !== 'suspended' ? (
                                    <button
                                      onClick={() => {
                                        setOpenMenuId(null);
                                        setSuspendModal({ id: org.id, name: org.name, reason: '' });
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-[12px] text-amber-700 hover:bg-amber-50 flex items-center gap-2"
                                    >
                                      <PauseCircle className="h-3.5 w-3.5" />
                                      Suspend
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setOpenMenuId(null);
                                        handleReactivate(org);
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-[12px] text-emerald-700 hover:bg-emerald-50 flex items-center gap-2"
                                    >
                                      <PlayCircle className="h-3.5 w-3.5" />
                                      Reactivate
                                    </button>
                                  )}
                                  <div className="border-t border-slate-100 my-1" />
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setDeleteModal({ id: org.id, name: org.name, confirmText: '' });
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-[12px] text-rose-700 hover:bg-rose-50 flex items-center gap-2"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    Delete…
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {planModal && (
        <Modal title={`Change plan — ${planModal.name}`} onClose={() => setPlanModal(null)}>
          <form onSubmit={submitPlanChange} className="space-y-3">
            <div>
              <label className="block text-[12px] font-semibold text-slate-600 mb-1">Plan</label>
              <select
                value={planModal.plan}
                onChange={(e) => setPlanModal({ ...planModal, plan: e.target.value })}
                className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
              >
                {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-slate-600 mb-1">Monthly amount (USD)</label>
              <input
                type="number"
                min={0}
                step={1}
                value={planModal.monthly_amount}
                onChange={(e) => setPlanModal({ ...planModal, monthly_amount: e.target.value })}
                className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-slate-600 mb-1">Reason (optional)</label>
              <input
                type="text"
                value={planModal.reason}
                onChange={(e) => setPlanModal({ ...planModal, reason: e.target.value })}
                placeholder="Annual negotiation, promo, etc."
                className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
              />
            </div>
            <FooterButtons onClose={() => setPlanModal(null)} working={working} submitLabel="Save" />
          </form>
        </Modal>
      )}

      {suspendModal && (
        <Modal title={`Suspend ${suspendModal.name}`} onClose={() => setSuspendModal(null)}>
          <form onSubmit={submitSuspend} className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-[12px] rounded-lg px-3 py-2 flex gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>Suspending will block all operator logins for this organization until reactivated.</span>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-slate-600 mb-1">Reason</label>
              <textarea
                required
                rows={3}
                value={suspendModal.reason}
                onChange={(e) => setSuspendModal({ ...suspendModal, reason: e.target.value })}
                placeholder="Non-payment, TOS violation, …"
                className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
              />
            </div>
            <FooterButtons onClose={() => setSuspendModal(null)} working={working} submitLabel="Suspend" tone="amber" />
          </form>
        </Modal>
      )}

      {deleteModal && (
        <Modal title={`Delete ${deleteModal.name}`} onClose={() => setDeleteModal(null)}>
          <div className="space-y-3">
            <div className="bg-rose-50 border border-rose-200 text-rose-800 text-[12px] rounded-lg px-3 py-2 flex gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                This cascades: facilities, users, customers, rentals, payments, invoices and leads tied to this
                organization will be permanently removed. This cannot be undone.
              </span>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-slate-600 mb-1">
                Type <span className="font-mono text-rose-700">{deleteModal.name}</span> to confirm
              </label>
              <input
                type="text"
                value={deleteModal.confirmText}
                onChange={(e) => setDeleteModal({ ...deleteModal, confirmText: e.target.value })}
                className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setDeleteModal(null)}
                className="px-3 py-1.5 text-[13px] text-slate-600 hover:bg-slate-50 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={submitDelete}
                disabled={working || deleteModal.confirmText !== deleteModal.name}
                className="px-3 py-1.5 text-[13px] font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-40"
              >
                {working ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 dark:bg-slate-950/60 backdrop-blur-sm px-4">
    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-800 w-full max-w-md">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800">
        <h3 className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
        <button onClick={onClose} className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  </div>
);

const FooterButtons = ({ onClose, working, submitLabel, tone = 'emerald' }) => {
  const tones = {
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    amber: 'bg-amber-600 hover:bg-amber-700',
  };
  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <button type="button" onClick={onClose} className="px-3 py-1.5 text-[13px] text-slate-600 hover:bg-slate-50 rounded-lg">
        Cancel
      </button>
      <button
        type="submit"
        disabled={working}
        className={`px-3 py-1.5 text-[13px] font-medium text-white ${tones[tone]} rounded-lg disabled:opacity-50`}
      >
        {working ? 'Saving…' : submitLabel}
      </button>
    </div>
  );
};

export default OrganizationsPanel;
